/**
 * Path post-processing (Movement spec §2.3) — raw A* output is a zig-zag staircase;
 * never feed it straight to the mover. This turns it into long straight runs, real
 * corners, and a per-skill-tier imperfect line so agents don't walk a robot-perfect
 * polyline.
 *
 * - String-pulling / funnel: drop any waypoint the previous kept one can see in a
 *   straight line (supercover LOS that refuses to cut a blocker's corner).
 * - Wall offset: push waypoints ~0.35 tiles off adjacent blockers so agents don't
 *   scrape along walls.
 * - Imperfection injection: jitter interior waypoints by N(0, σ), σ per tier
 *   (Rookie 0.45 … Elite 0.05). Humans don't walk the optimal line; Elites nearly do.
 *
 * Corner ROUNDING (§2.3) is deliberately not duplicated here: the steering mover
 * already slows into and accelerates out of turns (turn-angle speed scaling in
 * mover.ts), which produces the rounded path visually. Adding a second bezier layer
 * would double-smooth and fight the mover.
 */

import { NavGrid, tileIndex } from './grid';

export interface Pt { x: number; y: number; }

const blockedTile = (grid: NavGrid, tx: number, ty: number) =>
  tx < 0 || ty < 0 || tx >= grid.w || ty >= grid.h || grid.walkable[tileIndex(tx, ty, grid.w)] === 0;

/**
 * Supercover line-of-sight between two float points: every tile the segment touches
 * must be walkable, and a diagonal that would slip between two blockers' corners is
 * rejected (no corner-cut). Amanatides–Woo grid traversal.
 */
export function lineWalkable(grid: NavGrid, x0: number, y0: number, x1: number, y1: number): boolean {
  let tx = Math.floor(x0), ty = Math.floor(y0);
  const tx1 = Math.floor(x1), ty1 = Math.floor(y1);
  if (blockedTile(grid, tx, ty)) return false;
  const dx = x1 - x0, dy = y1 - y0;
  const stepX = Math.sign(dx), stepY = Math.sign(dy);
  const tDeltaX = dx !== 0 ? Math.abs(1 / dx) : Infinity;
  const tDeltaY = dy !== 0 ? Math.abs(1 / dy) : Infinity;
  let tMaxX = dx !== 0 ? (stepX > 0 ? (tx + 1 - x0) : (x0 - tx)) * tDeltaX : Infinity;
  let tMaxY = dy !== 0 ? (stepY > 0 ? (ty + 1 - y0) : (y0 - ty)) * tDeltaY : Infinity;
  let guard = 0;
  const limit = grid.w + grid.h + 4;
  while ((tx !== tx1 || ty !== ty1) && guard++ < limit) {
    if (Math.abs(tMaxX - tMaxY) < 1e-9) {
      // Exact corner crossing: both orthogonal cells must be open, or it's a cut.
      if (blockedTile(grid, tx + stepX, ty) || blockedTile(grid, tx, ty + stepY)) return false;
      tMaxX += tDeltaX; tMaxY += tDeltaY; tx += stepX; ty += stepY;
    } else if (tMaxX < tMaxY) { tMaxX += tDeltaX; tx += stepX; }
    else { tMaxY += tDeltaY; ty += stepY; }
    if (blockedTile(grid, tx, ty)) return false;
  }
  return true;
}

/** Drop every waypoint the last kept one can see straight through. */
export function stringPull(grid: NavGrid, path: Pt[]): Pt[] {
  if (path.length <= 2) return path.slice();
  const out: Pt[] = [path[0]];
  let anchor = 0;
  for (let i = 2; i < path.length; i++) {
    if (!lineWalkable(grid, path[anchor].x, path[anchor].y, path[i].x, path[i].y)) {
      out.push(path[i - 1]);
      anchor = i - 1;
    }
  }
  out.push(path[path.length - 1]);
  return out;
}

/** Push interior waypoints ~`dist` tiles away from any orthogonally-adjacent blocker. */
export function wallOffset(grid: NavGrid, path: Pt[], dist = 0.35): Pt[] {
  if (path.length <= 2) return path.slice();
  const out: Pt[] = [path[0]];
  for (let i = 1; i < path.length - 1; i++) {
    const p = path[i];
    const tx = Math.floor(p.x), ty = Math.floor(p.y);
    let px = 0, py = 0;
    if (blockedTile(grid, tx + 1, ty)) px -= 1;
    if (blockedTile(grid, tx - 1, ty)) px += 1;
    if (blockedTile(grid, tx, ty + 1)) py -= 1;
    if (blockedTile(grid, tx, ty - 1)) py += 1;
    const l = Math.hypot(px, py);
    if (l > 0) {
      const nx = p.x + (px / l) * dist, ny = p.y + (py / l) * dist;
      // Only accept the nudge if it stays on a walkable tile.
      out.push(blockedTile(grid, Math.floor(nx), Math.floor(ny)) ? p : { x: nx, y: ny });
    } else out.push(p);
  }
  out.push(path[path.length - 1]);
  return out;
}

// Box-Muller gaussian from a uniform rng.
function gaussian(rng: () => number, sigma: number): number {
  const u = Math.max(1e-9, rng()), v = rng();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v) * sigma;
}

/** Jitter interior waypoints by N(0, σ) — the per-tier "humans don't walk straight". */
export function injectImperfection(grid: NavGrid, path: Pt[], sigma: number, rng: () => number): Pt[] {
  if (sigma <= 0 || path.length <= 2) return path.slice();
  const out: Pt[] = [path[0]];
  for (let i = 1; i < path.length - 1; i++) {
    const p = path[i];
    const nx = p.x + gaussian(rng, sigma), ny = p.y + gaussian(rng, sigma);
    out.push(blockedTile(grid, Math.floor(nx), Math.floor(ny)) ? p : { x: nx, y: ny });
  }
  out.push(path[path.length - 1]);
  return out;
}

export interface SmoothOptions { sigma?: number; wallOffset?: number; rng?: () => number; }

/** Full post-process: string-pull → wall offset → imperfection. */
export function smoothPath(grid: NavGrid, raw: Pt[], opts: SmoothOptions = {}): Pt[] {
  let p = stringPull(grid, raw);
  p = wallOffset(grid, p, opts.wallOffset ?? 0.35);
  if (opts.sigma && opts.rng) p = injectImperfection(grid, p, opts.sigma, opts.rng);
  return p;
}

/** Per-tier imperfection sigma (spec §2.3). */
export const TIER_SIGMA: Record<string, number> = { rookie: 0.45, regular: 0.25, veteran: 0.12, elite: 0.05 };
